import type { CarState, HitType, ObservationVersion, SensorObstacle, SensorReading, Vec2 } from "../types";
import { add, clip, dot, headingVector, scale, sub } from "./math";
import { TrackRuntime } from "./track";

function rayCircleDistance(origin: Vec2, direction: Vec2, center: Vec2, radius: number): number | null {
  const offset = sub(center, origin);
  const along = dot(offset, direction);
  const perpendicularSquared = dot(offset, offset) - along * along;
  const radiusSquared = radius * radius;
  if (perpendicularSquared > radiusSquared) return null;
  const halfChord = Math.sqrt(Math.max(0, radiusSquared - perpendicularSquared));
  const near = along - halfChord;
  const far = along + halfChord;
  if (far < 0) return null;
  return Math.max(0, near);
}

export function readSensors(
  car: CarState,
  track: TrackRuntime,
  anglesDegrees: number[],
  maxRange: number,
  obstacles: SensorObstacle[] = [],
): SensorReading[] {
  return anglesDegrees.map((angleDegrees) => {
    const direction = headingVector(car.heading + angleDegrees * Math.PI / 180);
    const worldHit = track.raycastWorld(car.position, direction, maxRange);
    let distance = worldHit.distance;
    let hitType: HitType = distance < maxRange - 1e-9 ? "TRACK" : "NONE";
    let objectId = worldHit.objectId;
    for (const obstacle of obstacles) {
      const candidate = rayCircleDistance(car.position, direction, obstacle.center, obstacle.radius);
      if (candidate !== null && candidate < distance) {
        distance = candidate;
        hitType = obstacle.hitType;
        objectId = obstacle.objectId;
      }
    }
    return {
      angleDegrees,
      distance,
      normalized: clip(distance / maxRange, 0, 1),
      endpoint: add(car.position, scale(direction, distance)),
      hitType,
      objectId,
    };
  });
}

export function encodeObservation(
  readings: SensorReading[],
  normalizedSpeed: number,
  version: ObservationVersion,
  route?: { normalizedOffset: number; headingError: number },
): number[] {
  if (version === "perception-v1") return [...readings.map((reading) => reading.normalized), normalizedSpeed];
  const values: number[] = [];
  for (const reading of readings) {
    values.push(
      reading.normalized,
      reading.hitType === "TRACK" ? 1 : 0,
      reading.hitType === "VEHICLE" ? 1 : 0,
      reading.hitType === "RABBIT" ? 1 : 0,
    );
  }
  values.push(normalizedSpeed);
  if (version === "perception-v3") {
    if (!route) throw new Error("Perception v3 requires explicit route context.");
    values.push(
      Math.max(-1, Math.min(1, route.normalizedOffset)),
      Math.sin(route.headingError),
      Math.cos(route.headingError),
    );
  }
  return values;
}
